import { useState, useCallback, useMemo, useRef } from "react";
import html2canvas from "html2canvas-pro";
import { PATH } from "../../../../constantes/constantes";
import ShareImage from "../shareImage/shareImage";
import "./shareButton.sass";

const SHARE_URL = PATH + "/simulador-mundial-2026/";
const SHARE_HASHTAG = "#SimuladorClaroSports";

// Fuera de pantalla, invisible pero el DOM la renderiza (background-image se carga)
const HIDDEN_STYLE = {
  position: "fixed",
  left: "-9999px",
  top: 0,
  width: "1080px",
  height: "1920px",
  overflow: "hidden",
  pointerEvents: "none",
  zIndex: -1,
  visibility: "hidden",
};

export default function ShareButton({ winner, qualifyingState }) {
  const [isCapturing, setIsCapturing] = useState(false);
  const shareImageRef = useRef(null);

  // En iOS/macOS, ocultar el botón si no es Safari
  const isHidden = useMemo(() => {
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) || navigator.maxTouchPoints > 1;
    const isMac = /Macintosh|MacIntel/.test(ua);
    if (!isIOS && !isMac) return false;
    if (isIOS) return /CriOS|FxiOS|OPiOS|EdgiOS|GSA/.test(ua);
    return /Chrome|Firefox|Edg|OPR/.test(ua);
  }, []);

  const generateImage = useCallback(async () => {
    const element = shareImageRef.current;
    if (!element) return null;

    // Hacer visible sin mover al viewport
    element.style.visibility = "visible";

    // Forzar carga de todas las <img> que aún no estén listas
    const imgs = Array.from(element.querySelectorAll("img"));
    await Promise.all(
      imgs.map((img) =>
        img.complete
          ? Promise.resolve()
          : new Promise((res) => { img.onload = res; img.onerror = res; })
      )
    );

    // Doble rAF para que el layout se estabilice tras hacerse visible
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

    try {
      const canvas = await html2canvas(element, {
        backgroundColor: null,
        scale: 1,
        useCORS: true,
        allowTaint: true,
        logging: false,
        scrollX: 0,
        scrollY: 0,
        width: 1080,
        height: 1920,
        windowWidth: 1080,
        windowHeight: 1920,
      });

      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), "image/png", 1.0);
      });
    } catch (err) {
      console.error("Error al capturar imagen:", err);
      return null;
    } finally {
      // Volver a ocultar
      element.style.visibility = "hidden";
    }
  }, []);

  const blobToBase64 = useCallback((blob) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  }, []);

  const isInIframe = window.self !== window.top;

  const handleShare = useCallback(async () => {
    if (!winner || winner === "TBD" || isCapturing) return;
    setIsCapturing(true);

    try {
      const shareTitle = "Claro Sports | Últimas noticias, resultados e información deportiva";
      const shareText = `Mi ganador del Mundial 2026 es ${winner} ${SHARE_HASHTAG}`;
      const blob = await generateImage();

      if (isInIframe && blob) {
        const base64 = await blobToBase64(blob);
        window.parent.postMessage({
          type: "share-simulator",
          image: base64,
          title: shareTitle,
          text: shareText,
          url: SHARE_URL,
          winner,
        }, "*");
        return;
      }

      if (navigator.share && blob) {
        const file = new File([blob], "mi-prediccion-mundial-2026.png", { type: "image/png" });
        if (navigator.canShare?.({ files: [file] })) {
          await navigator.share({ title: shareTitle, text: shareText, url: SHARE_URL, files: [file] });
          return;
        }
      }

      if (navigator.share) {
        await navigator.share({ title: shareTitle, text: shareText, url: SHARE_URL });
        return;
      }

      // Fallback: descargar imagen
      if (blob) {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "mi-prediccion-mundial-2026.png";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }

      try {
        await navigator.clipboard.writeText(`${shareTitle}\n${shareText}\n${SHARE_URL}`);
      } catch { /* silenciar */ }
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Error al compartir:", err);
      }
    } finally {
      setIsCapturing(false);
    }
  }, [winner, isCapturing, generateImage, blobToBase64, isInIframe]);

  if (!winner || winner === "TBD" || isHidden) return null;

  return (
    <>
      {/* ShareImage siempre en el DOM, oculta, lista para capturar*/}
      <div ref={shareImageRef} style={HIDDEN_STYLE}>
        <ShareImage qualifyingState={qualifyingState}  />
      </div>

      <button
        className={`ShareButton ${isCapturing ? "capturing" : ""}`}
        onClick={handleShare}
        disabled={isCapturing}
        aria-label={`Compartir predicción: mi ganador es ${winner}`}
      >
        {isCapturing ? (
          <span className="ShareButton__loader" />
        ) : (
          <svg className="ShareButton__icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 8C19.6569 8 21 6.65685 21 5C21 3.34315 19.6569 2 18 2C16.3431 2 15 3.34315 15 5C15 5.12548 15.0077 5.24917 15.0227 5.37061L8.08261 9.19071C7.54305 8.46738 6.6793 8 5.7 8C4.20883 8 3 9.20883 3 10.7C3 12.1912 4.20883 13.4 5.7 13.4C6.6793 13.4 7.54305 12.9326 8.08261 12.2093L15.0227 16.0294C15.0077 16.1508 15 16.2745 15 16.4C15 18.0569 16.3431 19.4 18 19.4C19.6569 19.4 21 18.0569 21 16.4C21 14.7431 19.6569 13.4 18 13.4C17.0207 13.4 16.157 13.8674 15.6174 14.5907L8.67739 10.7706C8.69234 10.6492 8.7 10.5255 8.7 10.4C8.7 10.2745 8.69234 10.1508 8.67739 10.0294L15.6174 6.20929C16.157 6.93262 17.0207 7.4 18 7.4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        )}
        <span className="ShareButton__text">{isCapturing ? "Capturando..." : "Compartir"}</span>
      </button>
    </>
  );
}